import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faSliders } from "@fortawesome/pro-solid-svg-icons/faSliders";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import { defineMessages, useIntl } from "react-intl";

import LeaderboardMetricFieldName from "@mapmycustomers/shared/enum/fieldModel/LeaderboardMetricFieldName";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { Button, Labeled, SelectField } from "@mapmycustomers/ui";

import StageSelectBox from "@app/component/input/StageSelectBox";
import SourceType, { getSourceTypeDisplayName, SupportedSources } from "@app/enum/SourceType";
import PerformanceFilters from "@app/scene/dashboard/types/PerformanceFilters";
import { getActivityTypes } from "@app/store/activity";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import defaultSelectOptionMapper from "@app/util/mappers/defaultSelectOptionMapper";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import styles from "./Filters.module.scss";

const messages = defineMessages({
  activityTypes: {
    id: "dashboard.sidebar.filters.activityTypes",
    defaultMessage: "Activity Type(s)",
    description: "Title of the Activity Types field in Dashboard's Sidebar",
  },
  activityTypesPlaceholder: {
    id: "dashboard.sidebar.filters.activityTypes.placeholder",
    defaultMessage: "Any Type",
    description: "Placeholder of the Activity Types field in Dashboard's Sidebar",
  },
  funnels: {
    id: "dashboard.sidebar.filters.funnels",
    defaultMessage: "Funnel(s)",
    description: "Title of the Funnels field in Dashboard's Sidebar",
  },
  funnelsPlaceholder: {
    id: "dashboard.sidebar.filters.funnels.placeholder",
    defaultMessage: "All Funnels",
    description: "Placeholder of the Funnels field in Dashboard's Sidebar",
  },
  sources: {
    id: "dashboard.sidebar.filters.sources",
    defaultMessage: "Source(s)",
    description: "Title of the Sources field in Dashboard's Sidebar",
  },
  sourcesPlaceholder: {
    id: "dashboard.sidebar.filters.sources.placeholder",
    defaultMessage: "Any Source",
    description: "Placeholder of the Sources field in Dashboard's Sidebar",
  },
  stages: {
    id: "dashboard.sidebar.filters.stages",
    defaultMessage: "Stage(s)",
    description: "Title of the Stages field in Dashboard's Sidebar",
  },
  stagesPlaceholder: {
    id: "dashboard.sidebar.filters.stages.placeholder",
    defaultMessage: "All Stages",
    description: "Placeholder of the Stages field in Dashboard's Sidebar",
  },
  title: {
    id: "dashboard.sidebar.filters",
    defaultMessage: "Filter",
    description: "Filters title in Dashboard's Sidebar",
  },
});

const getSupportedFilters = (metric: LeaderboardMetricFieldName) => ({
  activityType: [
    LeaderboardMetricFieldName.ACTIVITIES_COMPLETED,
    LeaderboardMetricFieldName.CHECK_INS_COMPLETED,
  ].includes(metric),
  funnel: [
    LeaderboardMetricFieldName.DEALS_CLOSED_LOST,
    LeaderboardMetricFieldName.DEALS_CLOSED_WON,
    LeaderboardMetricFieldName.DEALS_CREATED,
    LeaderboardMetricFieldName.DEALS_ROTTING,
    LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE,
    LeaderboardMetricFieldName.REVENUE_CLOSED_LOST,
    LeaderboardMetricFieldName.REVENUE_CLOSED_WON,
  ].includes(metric),
  source: metric !== LeaderboardMetricFieldName.ROUTES_CREATED,
  stage: [
    LeaderboardMetricFieldName.DEALS_CREATED,
    LeaderboardMetricFieldName.DEALS_ROTTING,
    LeaderboardMetricFieldName.NEW_PIPELINE_REVENUE,
  ].includes(metric),
});

interface OwnProps {
  filters: PerformanceFilters;
  onFiltersChange?: (filters: PerformanceFilters) => void;
  rankingMetric: IField;
}

interface Props extends OwnProps {
  activityTypes: ActivityType[];
  funnels: Funnel[];
}

const Filters: FC<Props> = ({
  activityTypes,
  filters,
  funnels,
  onFiltersChange,
  rankingMetric,
}) => {
  const analytics = useExtendedAnalytics("Filter", useAnalytics());
  const intl = useIntl();

  const { activityType, funnel, source, stage } = getSupportedFilters(
    rankingMetric.name as LeaderboardMetricFieldName
  );

  const sourceOptions = useMemo(
    () =>
      Object.values(SourceType).map((value) => ({
        label: getSourceTypeDisplayName(intl, value),
        value,
      })),
    [intl]
  );

  const activityTypeOptions = useActivityTypeOptions(activityTypes);

  const funnelOptions = useMemo(() => funnels.map(defaultSelectOptionMapper), [funnels]);

  const handleSourceChange = useCallback(
    (sources: SupportedSources[]) => {
      analytics.using(["Source"]);
      onFiltersChange?.({ ...filters, sources });
    },
    [filters, onFiltersChange, analytics]
  );

  const handleActivityTypeChange = useCallback(
    (activityTypeIds: ActivityType["id"][]) => {
      analytics.using(["Activity Type"]);
      onFiltersChange?.({ ...filters, activityTypeIds });
    },
    [filters, onFiltersChange, analytics]
  );

  const handleFunnelsChange = useCallback(
    (funnelIds: Funnel["id"][]) => {
      analytics.using(["Funnel"]);
      onFiltersChange?.({ ...filters, funnelIds });
    },
    [filters, onFiltersChange, analytics]
  );

  const handleStagesChange = useCallback(
    (stageIds: Stage["id"][]) => {
      analytics.using(["Stage"]);
      onFiltersChange?.({ ...filters, stageIds });
    },
    [filters, onFiltersChange, analytics]
  );

  const noFiltersAllowed = !activityType && !funnel && !stage && !source;

  return (
    <Popover
      content={
        <div className={styles.overlay} onClick={stopEvents}>
          <span className={styles.title}>{intl.formatMessage(messages.title)}</span>
          {source && (
            <SelectField<SupportedSources[]>
              allowClear
              label={intl.formatMessage(messages.sources)}
              mode="multiple"
              onChange={handleSourceChange}
              options={sourceOptions}
              placeholder={intl.formatMessage(messages.sourcesPlaceholder)}
              value={filters.sources}
            />
          )}
          {activityType && (
            <SelectField<ActivityType["id"][]>
              allowClear
              label={intl.formatMessage(messages.activityTypes)}
              mode="multiple"
              onChange={handleActivityTypeChange}
              options={activityTypeOptions}
              placeholder={intl.formatMessage(messages.activityTypesPlaceholder)}
              value={filters.activityTypeIds}
            />
          )}
          {funnel && (
            <SelectField<Funnel["id"][]>
              allowClear
              label={intl.formatMessage(messages.funnels)}
              mode="multiple"
              onChange={handleFunnelsChange}
              options={funnelOptions}
              placeholder={intl.formatMessage(messages.funnelsPlaceholder)}
              value={filters.funnelIds}
            />
          )}
          {stage && (
            <Labeled label={intl.formatMessage(messages.stages)}>
              <StageSelectBox
                allowClear
                onChange={handleStagesChange}
                placeholder={intl.formatMessage(messages.stagesPlaceholder)}
                selectedFunnelsIds={filters.funnelIds}
                showArrow
                showSearch
                value={filters.stageIds}
              />
            </Labeled>
          )}
        </div>
      }
      destroyTooltipOnHide
      overlayClassName={styles.overlayContainer}
      placement="bottomLeft"
      title={null}
      trigger={noFiltersAllowed ? [] : ["click"]}
    >
      <Button
        disabled={noFiltersAllowed}
        icon={<FontAwesomeIcon icon={faSliders} />}
        iconOnly
        type="filled"
      />
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
  funnels: getFunnels(state),
  funnelStages: getFunnelStages(state),
});

export default connect(mapStateToProps)(Filters);
